import React, { useState, useEffect, useContext } from 'react';
import api from './api';
import { AuthContext } from './AuthContext';
import { ThemeContext } from './ThemeContext';

export default function AdminDashboard() {
  const { token, logout } = useContext(AuthContext);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);

  const [title, setTitle] = useState('');
  const [source, setSource] = useState('youtube');
  const [youtubeId, setYoutubeId] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [categoryId, setCategoryId] = useState('');
  
  // Category management state
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);

  useEffect(() => {
    fetchVideos();
    fetchCategories();
  }, []);

  const fetchCategories = () => {
    api
      .get('/api/categories', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setCategories(res.data))
      .catch(() => alert('Failed to load categories'));
  };
  
  const fetchVideos = () => {
    api
      .get('/api/videos', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setVideos(res.data));
  };

  const addCategory = async (e) => {
    e.preventDefault();
    
    if (!newCategoryName.trim()) {
      alert('Category name is required');
      return;
    }

    try {
      await api.post(
        '/api/categories',
        { name: newCategoryName, description: newCategoryDescription },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNewCategoryName('');
      setNewCategoryDescription('');
      setShowCategoryForm(false);
      fetchCategories();
      alert('Category created successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create category');
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category? This will fail if there are videos in this category.')) return;
    
    try {
      await api.delete(`/api/categories/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchCategories();
      alert('Category deleted successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete category');
    }
  };

  const addVideo = async e => {
    e.preventDefault();

    if (!title || (source === 'youtube' && !youtubeId) || (source === 'backblaze' && !videoUrl)) {
      alert('Please fill all required fields');
      return;
    }
    
    try {
      await api.post(
        '/api/videos',
        {
          title,
          source,
          youtube_id: source === 'youtube' ? youtubeId : null,
          video_url: source === 'backblaze' ? videoUrl : null,
          category_id: categoryId || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTitle('');
      setYoutubeId('');
      setVideoUrl('');
      setCategoryId('');
      fetchVideos();
      alert('Video added successfully!');
    } catch {
      alert('Failed to add video');
    }
  };

  const updateVideo = async (e) => {
    e.preventDefault();
    
    if (!editingVideo) return;

    try {
      await api.put(
        `/api/videos/${editingVideo.id}`,
        {
          title: editingVideo.title,
          category_id: editingVideo.category_id || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEditingVideo(null);
      fetchVideos();
      alert('Video updated successfully!');
    } catch {
      alert('Failed to update video');
    }
  };

  const deleteVideo = async id => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await api.delete(`/api/videos/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchVideos();
    } catch {
      alert('Failed to delete video');
    }
  };

  return (
    <div className="container mt-4">
      <div className="text-center mb-4">
        <img src="/logo192.png" alt="Kuppi Site Logo" className="my-2 d-block mx-auto" style={{ width: '150px', height: '130px' }} />
        <h1 className="fw-bold text-primary">Kuppi Site</h1>
      </div>
      
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Admin Dashboard</h3>
          <div className="d-flex gap-2">
            <button 
              className={`btn ${theme === 'dark' ? 'btn-outline-light' : 'btn-outline-dark'}`} 
              onClick={toggleTheme}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              <i className={`bi bi-${theme === 'dark' ? 'sun' : 'moon'} me-2`}></i>
              {theme === 'dark' ? 'Light' : 'Dark'}
            </button>
            <button className="btn btn-light" onClick={logout}>
              <i className="bi bi-box-arrow-right me-2"></i>Logout
            </button>
          </div>
        </div>
        
        <div className="card-body">
          {/* Category Management Section */}
          <div className="mb-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title mb-0">
                <i className="bi bi-folder me-2"></i>Manage Categories
              </h5>
              <button 
                className="btn btn-sm btn-primary"
                onClick={() => setShowCategoryForm(!showCategoryForm)}
              >
                <i className="bi bi-plus-circle me-2"></i>
                {showCategoryForm ? 'Cancel' : 'New Category'}
              </button>
            </div>

            {showCategoryForm && (
              <div className="card mb-3 bg-light">
                <div className="card-body">
                  <form onSubmit={addCategory}>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Category Name"
                          value={newCategoryName}
                          onChange={e => setNewCategoryName(e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Description (optional)"
                          value={newCategoryDescription}
                          onChange={e => setNewCategoryDescription(e.target.value)}
                        />
                      </div>
                      <div className="col-md-2">
                        <button type="submit" className="btn btn-success w-100">
                          <i className="bi bi-check-circle me-1"></i>Create
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {categories.length === 0 ? (
              <div className="alert alert-info">No categories yet. Create one to organize your videos!</div>
            ) : (
              <div className="row g-2">
                {categories.map(category => (
                  <div key={category.id} className="col-md-6 col-lg-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="card-title mb-1">
                              <i className="bi bi-folder-fill me-2 text-primary"></i>
                              {category.name}
                            </h6>
                            {category.description && (
                              <p className="card-text small text-muted mb-0">{category.description}</p>
                            )}
                          </div>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteCategory(category.id)}
                            title="Delete category"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <hr className="my-4" />

          {/* Video Management Section */}
          <div className="row">
            <div className="col-12">
              <h5 className="card-title mb-3">Add New Video</h5>
              <form onSubmit={addVideo} className="mb-4">
                <div className="row g-3">
                  <div className="col-md-5">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Video Title"
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      required
                    />
                  </div>
                  
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={categoryId}
                      onChange={e => setCategoryId(e.target.value)}
                    >
                      <option value="">Select Category (Optional)</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="col-md-2">
                    <select
                      className="form-select"
                      value={source}
                      onChange={e => setSource(e.target.value)}
                    >
                      <option value="youtube">YouTube</option>
                      <option value="backblaze">Backblaze</option>
                    </select>
                  </div>
                  
                  <div className="col-md-2">
                    <button type="submit" className="btn btn-success w-100">
                      <i className="bi bi-plus-circle me-2"></i>Add
                    </button>
                  </div>
                  
                  <div className="col-12 mt-3">
                    {source === 'youtube' && (
                      <input
                        type="text"
                        className="form-control"
                        placeholder="YouTube Video ID (e.g., dQw4w9WgXcQ)"
                        value={youtubeId}
                        onChange={e => setYoutubeId(e.target.value)}
                        required
                      />
                    )}

                    {source === 'backblaze' && (
                      <input
                        type="url"
                        className="form-control"
                        placeholder="Direct Video URL"
                        value={videoUrl}
                        onChange={e => setVideoUrl(e.target.value)}
                        required
                      />
                    )}
                  </div>
                </div>
              </form>
              
              <h5 className="card-title mb-3">Manage Videos</h5>
              {videos.length === 0 ? (
                <div className="alert alert-info">No videos available</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Source</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {videos.map(video => (
                        <tr key={video.id}>
                          <td>
                            {editingVideo?.id === video.id ? (
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={editingVideo.title}
                                onChange={e => setEditingVideo({...editingVideo, title: e.target.value})}
                              />
                            ) : (
                              video.title
                            )}
                          </td>
                          <td>
                            {editingVideo?.id === video.id ? (
                              <select
                                className="form-select form-select-sm"
                                value={editingVideo.category_id || ''}
                                onChange={e => setEditingVideo({...editingVideo, category_id: e.target.value || null})}
                              >
                                <option value="">No Category</option>
                                {categories.map(cat => (
                                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                              </select>
                            ) : (
                              <span className={`badge ${video.category_name ? 'bg-primary' : 'bg-secondary'}`}>
                                {video.category_name || 'Uncategorized'}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="badge bg-info text-dark">{video.source}</span>
                          </td>
                          <td>
                            {editingVideo?.id === video.id ? (
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-success"
                                  onClick={updateVideo}
                                >
                                  <i className="bi bi-check-lg"></i> Save
                                </button>
                                <button
                                  className="btn btn-secondary"
                                  onClick={() => setEditingVideo(null)}
                                >
                                  <i className="bi bi-x-lg"></i> Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-outline-primary"
                                  onClick={() => setEditingVideo(video)}
                                >
                                  <i className="bi bi-pencil me-1"></i>Edit
                                </button>
                                <button
                                  className="btn btn-danger"
                                  onClick={() => deleteVideo(video.id)}
                                >
                                  <i className="bi bi-trash me-1"></i>Delete
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
